import Box from '@mui/material/Box';
import { type ClipboardEvent, type KeyboardEvent, useRef } from 'react';
import { fonts, radius } from '@/shared/theme';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  label: string;
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

/** Row of single-digit boxes for one-time codes; supports typing, backspace, arrows and paste. */
export function OtpInput({
  value,
  onChange,
  length = 6,
  label,
  error,
  disabled,
  autoFocus,
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const focus = (i: number) => refs.current[Math.max(0, Math.min(length - 1, i))]?.focus();

  const setAt = (i: number, digit: string) => {
    const next = digits.slice();
    next[i] = digit;
    onChange(next.join('').slice(0, length));
  };

  const onKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) setAt(i, '');
      else if (i > 0) {
        setAt(i - 1, '');
        focus(i - 1);
      }
    } else if (e.key === 'ArrowLeft') focus(i - 1);
    else if (e.key === 'ArrowRight') focus(i + 1);
    else if (/^\d$/.test(e.key)) {
      e.preventDefault();
      setAt(i, e.key);
      focus(i + 1);
    }
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    onChange(pasted);
    focus(pasted.length);
  };

  return (
    <Box
      role="group"
      aria-label={label}
      sx={{ display: 'flex', gap: { xs: 1, sm: 1.25 }, justifyContent: 'center' }}
    >
      {digits.map((digit, i) => (
        <Box
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length positional inputs
          key={i}
          component="input"
          ref={(el: HTMLInputElement | null) => {
            refs.current[i] = el;
          }}
          value={digit}
          onChange={(e) => {
            // Mobile keyboards and autofill deliver the value via change events.
            const typed = e.target.value.replace(/\D/g, '');
            if (typed.length > 1) onChange(typed.slice(0, length));
            else if (typed) {
              setAt(i, typed);
              focus(i + 1);
            }
          }}
          onKeyDown={onKeyDown(i)}
          onPaste={onPaste}
          onFocus={(e) => e.currentTarget.select()}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          aria-label={`${label} ${i + 1}`}
          aria-invalid={error || undefined}
          sx={(theme) => ({
            width: { xs: 42, sm: 48 },
            height: { xs: 50, sm: 56 },
            textAlign: 'center',
            fontFamily: fonts.head,
            fontWeight: 800,
            fontSize: 22,
            color: theme.vars.palette.md.text,
            background: theme.vars.palette.md.panel,
            border: `1px solid ${error ? theme.vars.palette.md.bad : theme.vars.palette.md.border}`,
            borderRadius: radius.control,
            outline: 0,
            transition: 'box-shadow .14s',
            '&:focus': {
              borderColor: theme.vars.palette.md.primary,
              boxShadow: `0 0 0 4px ${theme.vars.palette.md.primary050}`,
            },
            '&:disabled': { opacity: 0.55 },
          })}
        />
      ))}
    </Box>
  );
}
